/**
 * Shared documents section for entity edit panels (18.12).
 * Upload, download, delete files linked to an entity.
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchFiles, uploadFile, uploadFileFromUrl, deleteFile, openFileInTab } from '../../api/files';

export default function PanelDocuments({ entityType, entityId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const load = useCallback(async () => {
    if (!entityId) return;
    try {
      const data = await fetchFiles({ linked_entity_type: entityType, linked_entity_id: entityId });
      setFiles(data);
    } catch {
      // ignore
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  async function doUpload(file) {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('linked_entity_type', entityType);
      formData.append('linked_entity_id', entityId);
      formData.append('filename', file.name);
      await uploadFile(formData);
      load();
    } catch {
      // ignore
    } finally {
      setUploading(false);
    }
  }

  function handleUpload(e) {
    doUpload(e.target.files?.[0]);
    e.target.value = '';
  }

  async function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    // Check for dropped files first
    if (e.dataTransfer?.files?.length > 0) {
      doUpload(e.dataTransfer.files[0]);
      return;
    }

    // Check for dropped URL (dragged link from browser)
    const url = e.dataTransfer?.getData('text/uri-list') || e.dataTransfer?.getData('text/plain') || '';
    const trimmedUrl = url.trim().split('\n')[0].trim(); // take first URL if multiple
    if (trimmedUrl && /^https?:\/\//i.test(trimmedUrl)) {
      doUploadFromUrl(trimmedUrl);
    }
  }

  async function doUploadFromUrl(url) {
    setUploading(true);
    try {
      await uploadFileFromUrl({
        url,
        linked_entity_type: entityType,
        linked_entity_id: entityId,
      });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upload from URL');
    } finally {
      setUploading(false);
    }
  }

  async function handleOpen(file) {
    try {
      await openFileInTab(file.id);
    } catch {
      alert('Failed to open file');
    }
  }

  async function handleDelete(fileId) {
    if (!confirm('Delete this file?')) return;
    try {
      await deleteFile(fileId);
      load();
    } catch {
      // ignore
    }
  }

  if (!entityId) return null;

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Documents</h4>
        <label className="text-xs text-blue-600 hover:text-blue-700 cursor-pointer">
          <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
          {uploading ? 'Uploading...' : '+ Upload'}
        </label>
      </div>
      {/* Drop zone */}
      <div
        className={`rounded-md border-2 border-dashed px-3 py-3 mb-2 text-center text-xs transition-colors ${
          dragOver ? 'border-blue-400 bg-blue-50 text-blue-600' : 'border-gray-200 text-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
      >
        {uploading ? 'Uploading...' : dragOver ? 'Drop file or link here' : 'Drag files or links here to upload'}
      </div>
      {files.length === 0 ? (
        <p className="text-xs text-gray-400">No documents.</p>
      ) : (
        <div className="space-y-1">
          {files.map((file) => (
            <div key={file.id} className="flex items-center justify-between py-1 text-xs">
              <button
                onClick={() => handleOpen(file)}
                className="text-blue-700 hover:text-blue-900 hover:underline truncate flex-1 text-left font-medium"
                title={`Open ${file.filename || file.name}`}
              >
                {file.filename || file.name}
              </button>
              <div className="flex gap-1 ml-2">
                <button onClick={() => handleOpen(file)} className="text-blue-600 hover:text-blue-700">Open</button>
                <button onClick={() => handleDelete(file.id)} className="text-red-500 hover:text-red-700">Del</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
